import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Eye, PlusCircle, Trash2 } from "lucide-react";
import Layout from "@/components/Layout";
import RequireAuth from "@/components/RequireAuth";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const History = () => {
  const [reports, setReports] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();

  const handleDeleteAll = async () => {
    if (!user) return;
    const { error } = await supabase.from("reports").delete().eq("user_id", user.id);
    if (error) {
      toast({ title: "Failed to delete all", description: error.message, variant: "destructive" });
      return;
    }
    setReports([]);
    toast({ title: "All reports deleted" });
  };

  const handleDelete = async (id: string) => {
    const { error } = await supabase.from("reports").delete().eq("id", id);
    if (error) {
      toast({ title: "Failed to delete", description: error.message, variant: "destructive" });
      return;
    }
    setReports((prev) => prev.filter((r) => r.id !== id));
    toast({ title: "Report deleted" });
  };

  useEffect(() => {
    const fetch = async () => {
      const { data } = await supabase
        .from("reports")
        .select("id, created_at, product_category, marketplace, status, evidence_sources_used, sources, pasted_amazon_reviews, market_radar_enabled, topic")
        .order("created_at", { ascending: false });
      setReports(data || []);
      setLoading(false);
    };
    fetch();
  }, []);

  return (
    <RequireAuth><Layout>
      <div className="dark-page min-h-[80vh]">
        <div className="mx-auto max-w-6xl px-4 md:px-6 py-8">
          <div className="mb-6 flex items-center justify-between gap-3">
            <h1 className="text-4xl font-extrabold tracking-tight text-white">Past Reports</h1>
            <div className="flex items-center gap-2">
              {reports.length > 0 && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm" className="border-white/10 text-slate-400 hover:text-red-400">
                      <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete all
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent className="bg-[#0f1629] border-white/10">
                    <AlertDialogHeader>
                      <AlertDialogTitle className="text-white">Delete all reports?</AlertDialogTitle>
                      <AlertDialogDescription>This will permanently remove all {reports.length} reports. This cannot be undone.</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel className="border-white/10 text-slate-300">Cancel</AlertDialogCancel>
                      <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={handleDeleteAll}>Delete all</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
              <Link
                to="/generate"
                className="btn-gradient inline-flex items-center gap-1.5 rounded-lg px-4 py-2 text-sm font-semibold transition"
              >
                <PlusCircle className="h-4 w-4" /> New
              </Link>
            </div>
          </div>

          {loading ? (
            <p className="text-center text-slate-400">Loading…</p>
          ) : reports.length === 0 ? (
            <div className="card-surface text-center py-12">
              <p className="mb-4 text-slate-400">No reports yet.</p>
              <Link
                to="/generate"
                className="btn-gradient inline-flex items-center gap-2 rounded-lg px-6 py-2.5 text-sm font-semibold transition"
              >
                Generate your first Copy Pack
              </Link>
            </div>
          ) : (
            <div className="space-y-3">
              {reports.map((r) => (
                <div key={r.id} className="card-surface flex flex-row items-center justify-between gap-4 !p-4">
                  <div className="min-w-0">
                    <h3 className="text-sm font-medium text-white truncate">{r.topic || r.product_category}</h3>
                    <p className="text-xs text-slate-400">
                      {new Date(r.created_at).toLocaleDateString()} · {(() => {
                        const esu = r.evidence_sources_used as any[] | null;
                        const src = r.sources as any[] | null;
                        if (esu?.length) return `${esu.length} sources`;
                        if (src?.length) return `${src.length} sources`;
                        return "Evidence: not recorded";
                      })()}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {r.product_category && r.product_category !== "Other" && (
                      <Badge className="bg-teal-500/15 text-teal-300 border-0 text-xs">{r.product_category}</Badge>
                    )}
                    {r.market_radar_enabled && (
                      <Badge className="border-white/10 text-slate-400 bg-transparent text-xs">Compared</Badge>
                    )}
                    {(() => {
                      const hasWeb = !!(r.evidence_sources_used as any[] | null)?.length || !!(r.sources as any[] | null)?.length;
                      const hasPasted = !!r.pasted_amazon_reviews;
                      if (hasWeb && hasPasted) return <Badge className="border-white/10 text-slate-400 bg-transparent text-xs">Both</Badge>;
                      if (hasPasted) return <Badge className="border-white/10 text-slate-400 bg-transparent text-xs">Amazon pasted reviews</Badge>;
                      if (hasWeb) return <Badge className="border-white/10 text-slate-400 bg-transparent text-xs">Web chatter</Badge>;
                      return null;
                    })()}
                    <Badge className={`text-xs border-0 ${r.status === "complete" ? "bg-emerald-500/15 text-emerald-300" : r.status === "failed" ? "bg-red-500/15 text-red-300" : "bg-amber-500/15 text-amber-300"}`}>
                      {r.status}
                    </Badge>
                    <Link
                      to={r.status === "pending" ? `/processing/${r.id}` : `/report/${r.id}`}
                      className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition"
                    >
                      <Eye className="h-4 w-4" />
                    </Link>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <button className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 hover:text-red-400 hover:bg-white/5 transition">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </AlertDialogTrigger>
                      <AlertDialogContent className="bg-[#0f1629] border-white/10">
                        <AlertDialogHeader>
                          <AlertDialogTitle className="text-white">Delete this report?</AlertDialogTitle>
                          <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel className="border-white/10 text-slate-300">Cancel</AlertDialogCancel>
                          <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white" onClick={() => handleDelete(r.id)}>Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout></RequireAuth>
  );
};

export default History;
